import { GridValidRowModel } from "@mui/x-data-grid";
import { SlateResponse } from "../common/store/types";
import { isInLineup } from "../utils/showdownHandler";
import { LateSwapLineup, LateSwapPlayer } from "./store/types";

export const getBoostedLineupRoi = (lineup: LateSwapLineup, slate: SlateResponse, boostedPlayers: { [key: string]: number }, boostedStacksDict?: { [key: string]: number }) => {
    const totalBoost = getBoostRoiForLineup(lineup, slate, boostedPlayers, boostedStacksDict);
    return totalBoost + lineup?.simLineupRoi;
  }

export const getBoostRoiForLineup = (lineup: LateSwapLineup, slate: SlateResponse, boostedPlayersDict: { [key: string]: number }, boostedStacksDict?: { [key: string]: number }) => {
  let totalBoost = 0;
  const playersArray = lineup.exportableLineup.split(',');
  playersArray.forEach((player) => {
    if (boostedPlayersDict[player]) {
      totalBoost += boostedPlayersDict[player];
    }
  })
  
  if (boostedStacksDict) {
    Object.keys(boostedStacksDict).forEach((stack) => {
      if (lineup.stacks.find(x => x.team === stack)) {
        totalBoost += boostedStacksDict[stack];
      }
    })
  }
  totalBoost = totalBoost / 100;

  return totalBoost;
}

export const getLineupUniqueHash = (lineup: LateSwapLineup) => {
    return lineup.lateSwapId + lineup.lineupHashCode + lineup.contestId;
}

export const lateSwapSupportedSport =(sport: string) => {
    return ['NBA', 'NFL'].includes(sport.toUpperCase());
}


export const canFavoriteLineup = (lineup: LateSwapLineup, currPlayerExposure: { [key: string]: number }, currStackExposure: { [key: string]: number }, lockedPlayers: { [key: string]: boolean}, totalLineups: number, selectedSlate: SlateResponse, playerExposureDict: { [key: string]: number}, stackExposureDict: { [key: string]: number}, currentFavoritedLineups: string[], allowDupes: boolean ) => {
  const currentPlayerExposures = Object.entries(currPlayerExposure);
  const currentStackExposures = Object.entries(currStackExposure);
  
  const globalPlayerExposure = playerExposureDict['global'] || 100;
  const globalStackExposure = stackExposureDict['global'] || 100;

  const playerExposuresToIndexIfAdded = currentPlayerExposures
  .filter(x => {
    return lockedPlayers[x[0]] !== undefined
    && !lockedPlayers[x[0]]
    && isInLineup(lineup.exportableLineup, x[0], selectedSlate.site, selectedSlate.sport)
  });

  const stackExposuresToIndexIfAdded = currentStackExposures
  .filter(x => 
    {
      return lineup
      .stacks
      .find(y => (
        (selectedSlate.sport?.toLowerCase() === 'mlb' && y.count > 2)
      || (selectedSlate.sport?.toLowerCase() === 'nfl' && y.count > 0)
      ) && y.team === x[0])
    }
  );

  const stackCanBeAddedWithoutGoingOver = stackExposuresToIndexIfAdded
  .filter(x => {
    return (
    (x[1] + 1)
    > (
      Math.min(
        stackExposureDict[x[0]] === undefined ? 100 : stackExposureDict[x[0]], globalStackExposure
      ) * totalLineups / 100
      )
    )}
  ).length > 0  // See if any stack exposures would be exceeded

  if(stackCanBeAddedWithoutGoingOver) {
    return false;
  }

  if (playerExposuresToIndexIfAdded.filter(x => (x[1] + 1)
    > (Math.min(playerExposureDict[x[0]] === undefined ? 100 : playerExposureDict[x[0]], globalPlayerExposure) * totalLineups / 100)).length > 0 // See if any player exposures would be exceeded
    || (!allowDupes && currentFavoritedLineups.includes(lineup.lineupHashCode))) { // See if any stack exposures would be exceeded
      return false;
  }
  return true;
}

export const indexExposures = (lineup: LateSwapLineup, currPlayerExposure: { [key: string]: number }, lockedPlayers: { [key: string]: boolean}, currStackExposure: { [key: string]: number }, selectedSlate: SlateResponse, ) => {
  const lineupString = lineup.exportableLineup as string;
  lineupString.split(',').forEach((x, i) => {
    const key = getExposuresKey(x, i, selectedSlate);
    currPlayerExposure[key] = (currPlayerExposure[key] || 0) + 1;
  })
  lineup.stacks.forEach(y => {
    if(
      (selectedSlate.sport?.toLowerCase() === 'mlb' && y.count > 2) || (selectedSlate.sport?.toLowerCase() === 'nfl' && y.count > 0)
    ) {
      return currStackExposure[y.team] = (currStackExposure[y.team] || 0) + 1;
    }
    return;
  });
  return { currPlayerExposure, currStackExposure };
}

const getExposuresKey = (player: string, index: number, selectedSlate: SlateResponse) => {
  if (selectedSlate?.type?.toUpperCase() === 'SHOWDOWN') {
    return index === 0 ? `${player}||CPT` : `${player}||FLEX`;
  }
  return player;
}

export const deIndexExposures = (lineup: LateSwapLineup, currPlayerExposure: { [key: string]: number }, lockedPlayers: { [key: string]: boolean}, selectedSlate: SlateResponse, ) => {
  const lineupString = lineup.exportableLineup as string;
  lineupString?.split(',').forEach(x => {
    if (lockedPlayers[x] !== undefined && !lockedPlayers[x]) { // Don't limit exposures of locked players
      currPlayerExposure[x] = (currPlayerExposure[x] || 0) - 1;
    }
  })
  return currPlayerExposure;
}


export const findLineupsToFavorite = (lineups: LateSwapLineup[], players: LateSwapPlayer[], alreadySavedLineups: any[], playerExposureDict: { [key: string]: number}, stackExposureDict: { [key: string]: number}, boostedPlayers: { [key: string]: number}, savedLineups:(LateSwapLineup | GridValidRowModel)[], contestName: string, selectedSlate: SlateResponse, allowDupes: boolean = true, boostedStacks?: { [key: string]: number}, uniquePlayersCount?: number): { lineups: (LateSwapLineup | GridValidRowModel)[], skippedDueToUniques: number } => {
    // Step 1: Create flat list of ALL lineups (original + all their options)
    type FlatLineupItem = {
      lineup: LateSwapLineup;
      groupId: string;
      originalParent: LateSwapLineup;
    };

    const flatList: FlatLineupItem[] = [];
    const filteredLineups = lineups.filter((lineup) => lineup?.associatedLateSwapEntries?.map((entry) => entry.contestName).includes(contestName) || contestName === 'all');

    filteredLineups.forEach((originalLineup) => {
      const groupId = originalLineup.lateSwapId + originalLineup.contestId;
      // Add all options to flat list
      originalLineup.lateSwapOptions?.forEach((option) => {
        flatList.push({
          lineup: option,
          groupId: groupId,
          originalParent: originalLineup
        });
      });
    });

    // Step 2: Add ROI boost and sort by final ROI (highest first)
    flatList.forEach((item) => {
      item.lineup.roiBoost = getBoostRoiForLineup(item.lineup, selectedSlate, boostedPlayers, boostedStacks);
      item.lineup.finalRoi = item.lineup.simLineupRoi + (item.lineup.roiBoost ?? 0);
    });

    flatList.sort((a, b) => (b.lineup.finalRoi || 0) - (a.lineup.finalRoi || 0));

    // Step 3: Initialize tracking variables
    const lockedPlayers = Object.fromEntries(players.map(x => [x.nameAndId, x.locked]));
    let topRoiLineups: LateSwapLineup[] = [];
    let savedLineupHashcodes = savedLineups.map(x => x.lineupHashCode);
    let favoritedLineupsStrings: string[] = savedLineups.map(x => x.exportableLineup);
    let currentPlayerExposure = initializeCurrentExposureDict(playerExposureDict);
    let currentStackExposure = initializeCurrentExposureDict(stackExposureDict);
    const totalLineupsToFavorite = filteredLineups.length;
    const processedGroups = new Set<string>();
    let skippedDueToUniques = 0;

    // Step 4: Iterate through flat list and select lineups
    // Track groups that failed due to uniqueness
    const groupsFailedUniqueness = new Set<string>();

    for (const item of flatList) {
      // Skip if this lineup's group was already processed (either favorited or failed)
      if (processedGroups.has(item.groupId)) {
        continue;
      }

      const lineup = item.lineup;

      // Check uniqueness if specified
      if (uniquePlayersCount && uniquePlayersCount > 0) {
        const isUniqueEnough = checkForUniqueness(
          lineup,
          uniquePlayersCount,
          favoritedLineupsStrings,
          selectedSlate
        );
        if (!isUniqueEnough) {
          groupsFailedUniqueness.add(item.groupId);
          continue; // Skip this lineup
        }
      }

      // Check if lineup can be favorited based on exposure caps and dupes
      if (canFavoriteLineup(lineup, currentPlayerExposure, currentStackExposure, lockedPlayers, totalLineupsToFavorite, selectedSlate, playerExposureDict, stackExposureDict, savedLineupHashcodes, allowDupes)) {
        // Add lineup metadata from parent
        lineup.associatedLateSwapEntries = item.originalParent?.associatedLateSwapEntries;
        lineup.parentRoi = item.originalParent?.simLineupRoi;
        lineup.lineupNumber = item.originalParent?.lineupNumber;

        // Add to favorites
        topRoiLineups.push(lineup);
        savedLineupHashcodes.push(lineup.lineupHashCode);
        favoritedLineupsStrings.push(lineup.exportableLineup);

        // Index exposures
        const { currPlayerExposure, currStackExposure } = indexExposures(lineup, currentPlayerExposure, lockedPlayers, currentStackExposure, selectedSlate);
        currentPlayerExposure = currPlayerExposure;
        currentStackExposure = currStackExposure;

        // Mark this group as processed (removes all options for this original lineup)
        processedGroups.add(item.groupId);
        // Remove from failed set if it was there
        groupsFailedUniqueness.delete(item.groupId);
      }
    }

    // Step 5: Handle groups that failed uniqueness - fallback to original lineup if all replacements have lower ROI
    if (uniquePlayersCount && uniquePlayersCount > 0 && groupsFailedUniqueness.size > 0) {
      groupsFailedUniqueness.forEach(failedGroupId => {
        // Find the original lineup for this group
        const originalLineup = filteredLineups.find(lineup => (lineup.lateSwapId + lineup.contestId) === failedGroupId);

        if (originalLineup && originalLineup.lateSwapOptions) {
          // Get the original lineup (marked with originalLineup = true)
          const original = originalLineup.lateSwapOptions.find(opt => opt.originalLineup);

          if (original) {
            // Calculate ROI for original
            const originalRoiBoost = getBoostRoiForLineup(original, selectedSlate, boostedPlayers, boostedStacks);
            const originalFinalRoi = original.simLineupRoi + originalRoiBoost;

            // Check if ALL replacement options have lower ROI than original
            const replacementOptions = originalLineup.lateSwapOptions.filter(opt => !opt.originalLineup);
            const allReplacementsHaveLowerRoi = replacementOptions.every(replacement => {
              const replacementRoiBoost = getBoostRoiForLineup(replacement, selectedSlate, boostedPlayers, boostedStacks);
              const replacementFinalRoi = replacement.simLineupRoi + replacementRoiBoost;
              return replacementFinalRoi < originalFinalRoi;
            });

            // If all replacements have lower ROI, ignore unique constraint and favorite the original
            if (allReplacementsHaveLowerRoi) {
              // Check if original can be favorited based on exposure caps and dupes (but NOT uniqueness)
              if (canFavoriteLineup(original, currentPlayerExposure, currentStackExposure, lockedPlayers, totalLineupsToFavorite, selectedSlate, playerExposureDict, stackExposureDict, savedLineupHashcodes, allowDupes)) {
                // Add lineup metadata
                original.associatedLateSwapEntries = originalLineup.associatedLateSwapEntries;
                original.parentRoi = originalLineup.simLineupRoi;
                original.lineupNumber = originalLineup.lineupNumber;

                // Add to favorites
                topRoiLineups.push(original);
                savedLineupHashcodes.push(original.lineupHashCode);
                favoritedLineupsStrings.push(original.exportableLineup);

                // Index exposures
                const { currPlayerExposure, currStackExposure } = indexExposures(original, currentPlayerExposure, lockedPlayers, currentStackExposure, selectedSlate);
                currentPlayerExposure = currPlayerExposure;
                currentStackExposure = currStackExposure;

                // Mark this group as processed
                processedGroups.add(failedGroupId);
                // Don't count this as skipped since we favorited the original
                groupsFailedUniqueness.delete(failedGroupId);
              }
            }
          }
        }
      });
    }

    // Count groups that failed uniqueness and never got favorited
    skippedDueToUniques = groupsFailedUniqueness.size;

    const filteredAlreadySavedLineups = topRoiLineups?.filter(x => !alreadySavedLineups.includes(x?.lateSwapId + x?.contestId));
    const saveableLineups = [
      ...savedLineups,
      ...filteredAlreadySavedLineups || [],
    ]

    return { lineups: saveableLineups, skippedDueToUniques };
}

export const initializeCurrentExposureDict = (exposureDict: { [key: string]: number }) => {
  const keys = Object.keys(exposureDict || {});
  const newExposureDict = {};
  keys.forEach(key => {
    newExposureDict[key] = 0;
  });
  return newExposureDict;
}

const checkForUniqueness = (lineup: LateSwapLineup, uniquePlayerPerLineup: number, currentlySavedLineups: string[], selectedSlate: SlateResponse) => {
  const hasCaptain = selectedSlate.type?.toLowerCase() === 'showdown';
  let players = lineup.exportableLineup.split(',');
  if(hasCaptain) {
    // If it's a showdown lineup, we need to account for the captain who is always the first player
    players = players.map((x, i) => i === 0 ? `${x}||CPT` : x);
  }

  const doesAnyOtherLineupInvalidateThisOne = currentlySavedLineups.some((x) => {
    let playersInLineup = x.split(',');
    if(hasCaptain) {
      // If it's a showdown lineup, we need to account for the captain who is always the first player
      playersInLineup = playersInLineup.map((x, i) => i === 0 ? `${x}||CPT` : x);
    }

    const uniquePlayers = players.filter(player => !playersInLineup.includes(player));

    return uniquePlayers.length < uniquePlayerPerLineup;
  })

  if(doesAnyOtherLineupInvalidateThisOne) {
    return false;
  }

  return true;
}